// ================ Action types ================ //

import { addMarketplaceEntities, getMarketplaceEntities } from 'ducks/marketplaceData.duck';
import { storableError } from 'util/errors';
import { denormalisedResponseEntities } from 'util/data';
import { parse } from 'util/urlHelpers';
import { PROMO_CODE_LISTING_TYPE } from '../config/constant';
import { deletePromoCodeApi } from '../api';

const PROMO_CODE_PER_PAGE = 20;

// ================ Reducer ================ //

const initialState = {
  promoCodeIds: [],
  fetchPromoCodesInProgress: false,
  fetchPromoCodesSuccess: false,
  fetchPromoCodesError: null,
  redirectedFromCreatePromoCodePage: false,
  pagination: { page: 1, perPage: PROMO_CODE_PER_PAGE, totalItems: 0, totalPages: 0 },
};

const FETCH_PROMO_CODES_IN_PROGRESS = 'app/PromoCodePage/FETCH_PROMO_CODES_IN_PROGRESS';
const FETCH_PROMO_CODES_SUCCESS = 'app/PromoCodePage/FETCH_PROMO_CODES_SUCCESS';
const FETCH_PROMO_CODES_ERROR = 'app/PromoCodePage/FETCH_PROMO_CODES_ERROR';

const DELETE_PROMO_CODE_IN_PROGRESS = 'app/PromoCodePage/DELETE_PROMO_CODE_IN_PROGRESS';
const DELETE_PROMO_CODE_SUCCESS = 'app/PromoCodePage/DELETE_PROMO_CODE_SUCCESS';
const DELETE_PROMO_CODE_ERROR = 'app/PromoCodePage/DELETE_PROMO_CODE_ERROR';

const UPDATE_PAGINATION = 'app/PromoCodePage/UPDATE_PAGINATION';
const UPDATE_PROMO_CODE_CLIENT_DATA = 'app/PromoCodePage/UPDATE_PROMO_CODE_CLIENT_DATA';
const REDIRECT_FROM_CREATING_PROMO_CODE = 'app/PromoCodePage/REDIRECT_FROM_CREATING_PROMO_CODE';
const COMPLETE_INDEX_NEW_PROMO_CODE = 'app/PromoCodePage/COMPLETE_INDEX_NEW_PROMO_CODE';

export default function promoCodePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_PROMO_CODES_IN_PROGRESS:
      return {
        ...state,
        fetchPromoCodesInProgress: true,
        fetchPromoCodesSuccess: false,
        fetchPromoCodesError: null,
      };
    case FETCH_PROMO_CODES_SUCCESS:
      return {
        ...state,
        fetchPromoCodesInProgress: false,
        fetchPromoCodesSuccess: true,
        promoCodeIds: payload,
      };
    case FETCH_PROMO_CODES_ERROR:
      return {
        ...state,
        fetchPromoCodesInProgress: false,
        fetchPromoCodesError: payload,
      };
    case DELETE_PROMO_CODE_IN_PROGRESS: {
      return {
        ...state,
        deletePromoCodeInProgress: true,
        deletePromoCodeSuccess: false,
        deletePromoCodeError: null,
      };
    }
    case DELETE_PROMO_CODE_SUCCESS: {
      return {
        ...state,
        deletePromoCodeInProgress: false,
        deletePromoCodeSuccess: true,
        promoCodeIds: payload,
      };
    }
    case DELETE_PROMO_CODE_ERROR: {
      return {
        ...state,
        deletePromoCodeInProgress: false,
        deletePromoCodeError: payload,
      };
    }
    case UPDATE_PAGINATION: {
      return {
        ...state,
        pagination: payload,
      };
    }
    case UPDATE_PROMO_CODE_CLIENT_DATA: {
      return {
        ...state,
        promoCodeIds: payload,
        redirectedFromCreatePromoCodePage: false,
      };
    }
    case REDIRECT_FROM_CREATING_PROMO_CODE: {
      return {
        ...state,
        redirectedFromCreatePromoCodePage: true,
      };
    }
    case COMPLETE_INDEX_NEW_PROMO_CODE: {
      return {
        ...state,
        redirectedFromCreatePromoCodePage: false,
      };
    }
    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchPromoCodesInProgress = () => ({
  type: FETCH_PROMO_CODES_IN_PROGRESS,
});

const fetchPromoCodesSuccess = (ids) => ({
  type: FETCH_PROMO_CODES_SUCCESS,
  payload: ids,
});

const fetchPromoCodesError = (error) => ({
  type: FETCH_PROMO_CODES_ERROR,
  payload: error,
});

const deletePromoCodeInProgress = () => ({
  type: DELETE_PROMO_CODE_IN_PROGRESS,
});

const deletePromoCodeSuccess = (payload) => ({
  type: DELETE_PROMO_CODE_SUCCESS,
  payload,
});

const deletePromoCodeError = (error) => ({
  type: DELETE_PROMO_CODE_ERROR,
  payload: error,
});

const updatePagination = (pagination) => ({
  type: UPDATE_PAGINATION,
  payload: pagination,
});

const updatePromoCodeClientData = (payload) => ({
  type: UPDATE_PROMO_CODE_CLIENT_DATA,
  payload,
});

const completeIndexNewPromoCode = () => ({
  type: COMPLETE_INDEX_NEW_PROMO_CODE,
});

export const redirectFromCreatingPromoCode = () => ({
  type: REDIRECT_FROM_CREATING_PROMO_CODE,
});

// ================ Thunks ================ //

const fetchPromoCodes =
  (params = {}) =>
  async (dispatch, getState, sdk) => {
    try {
      dispatch(fetchPromoCodesInProgress());
      const response = await sdk.ownListings.query({
        pub_listingType: PROMO_CODE_LISTING_TYPE,
        states: 'published',
        ...params,
      });
      const listings = denormalisedResponseEntities(response);
      const listingIds = listings
        .filter((l) => !l.attributes.deleted && !l.attributes.metadata.deleted)
        .map(({ id, type }) => {
          return { id, type };
        });
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchPromoCodesSuccess(listingIds));
      dispatch(updatePagination(response.data.meta));
      return listingIds;
    } catch (e) {
      return dispatch(fetchPromoCodesError(storableError(e)));
    }
  };

export const deletePromoCode = (id) => async (dispatch, getState) => {
  try {
    const { pagination } = getState().PromoCodePage;
    const { page, perPage } = pagination;

    const uuid = id?.uuid || id;
    dispatch(deletePromoCodeInProgress());
    await deletePromoCodeApi(uuid);
    await dispatch(fetchPromoCodes({ page, perPage }));
    const { promoCodeIds } = getState().PromoCodePage;
    const updatedPromoCodeIds = promoCodeIds.filter((item) => item.id.uuid !== uuid);
    dispatch(deletePromoCodeSuccess(updatedPromoCodeIds));
  } catch (e) {
    dispatch(deletePromoCodeError(storableError(e)));
  }
};

const loadDataFromCreatingCode = (params) => async (dispatch, getState) => {
  const promoCodeRefs = await dispatch(fetchPromoCodes(params));
  if (!promoCodeRefs) {
    return dispatch(completeIndexNewPromoCode());
  }
  const createdCodeRef = getState().EditPromoCodePage.createdPromoCodeRef;
  const isCreatedPromoCodeIndexed = promoCodeRefs.some(
    (ref) => ref.id.uuid === createdCodeRef.id.uuid
  );
  if (isCreatedPromoCodeIndexed) {
    return dispatch(completeIndexNewPromoCode());
  }
  const currentPagination = getState().PromoCodePage.pagination;
  const totalItems = currentPagination.totalItems + 1;
  const totalPages = Math.ceil(totalItems / currentPagination.perPage);
  const newPromoCodeRefs = promoCodeRefs.slice(0, currentPagination.perPage - 1);
  dispatch(
    updatePagination({
      ...currentPagination,
      paginationLimit: totalPages,
      totalItems,
      totalPages,
    })
  );
  return dispatch(updatePromoCodeClientData([createdCodeRef, ...newPromoCodeRefs]));
};

export const loadData = (params, search) => async (dispatch, getState) => {
  const isRedirectedFromCreatingPromoCode =
    getState().PromoCodePage.redirectedFromCreatePromoCodePage;
  const queryParams = parse(search);
  const { page = 1 } = queryParams;
  if (isRedirectedFromCreatingPromoCode) {
    return dispatch(loadDataFromCreatingCode({ page: 1, perPage: PROMO_CODE_PER_PAGE }));
  }
  return dispatch(fetchPromoCodes({ page, perPage: PROMO_CODE_PER_PAGE }));
};

export const selectPromoCodes = (state) => {
  const refs = state.PromoCodePage.promoCodeIds;
  const promoCodes = getMarketplaceEntities(state, refs);
  return promoCodes;
};
export const selectPromoCode = (id) => (state) => {
  const promoCodes = getMarketplaceEntities(state, [
    {
      id,
      type: 'ownListing',
    },
  ]);
  return promoCodes?.[0] || null;
};

export const selectDeletePromoCodeInProgress = (state) =>
  state.PromoCodePage.deletePromoCodeInProgress;
export const selectDeletePromoCodeSuccess = (state) => state.PromoCodePage.deletePromoCodeSuccess;
export const selectDeletePromoCodeError = (state) => state.PromoCodePage.deletePromoCodeError;

export const selectPromoCodePagePagination = (state) => state.PromoCodePage.pagination;
